"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";

/**
 * Herstelpoging voor een lijst die de server niet kon ophalen (degraded).
 *
 * Een mislukte ophaalactie is iets anders dan "echt leeg" (zie
 * CategoryPageClient). Terwijl de server-render degraded is, probeert dit
 * hooked maximaal 3 keer opnieuw, met 3, 8 en 20 s wachttijd ervoor (met
 * een beetje jitter naar beneden, zodat niet alle bezoekers na een uitrol
 * gelijktijdig terugkomen). Elke poging is een router.replace op dezelfde
 * pagina met `_heal=<tijdstempel>`: een unieke URL die zowel de
 * Cloudflare-kopie als de gecachete `_rsc`-variant omzeilt (de edge geeft
 * `_heal` private, no-store, zie de middleware).
 *
 * Pauze als het tabblad niet zichtbaar is: dan wacht de poging tot het
 * tabblad weer zichtbaar wordt. Zodra de payload niet meer degraded is,
 * gaat `_heal` met history.replaceState weer uit de adresbalk; overige
 * zoekparameters (filters, reisgegevens) blijven staan.
 */
export const HEAL_PARAM = "_heal";
export const HEAL_DELAYS_MS = [3000, 8000, 20000];
// Wachttijd na de laatste poging voordat we de eerlijke foutstaat tonen.
export const HEAL_FINAL_SETTLE_MS = 8000;
const JITTER_FRACTION = 0.2;

function withJitter(ms) {
  return Math.round(ms * (1 - Math.random() * JITTER_FRACTION));
}

/**
 * `options.delays`: eigen wachttijden (ms) per poging, in plaats van
 * HEAL_DELAYS_MS (de dealpagina gebruikt 2, 5 en 12 s, zie DealTransientHeal).
 */
export function useListingHeal(degraded, { delays = HEAL_DELAYS_MS } = {}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchString = searchParams ? searchParams.toString() : "";

  const [attempts, setAttempts] = useState(0);
  const [failed, setFailed] = useState(false);
  // Teller terug zodra de lijst niet meer degraded is (state-aanpassing tijdens
  // render, het door React bedoelde patroon; geen setState in een effect).
  const [prevDegraded, setPrevDegraded] = useState(degraded);
  if (prevDegraded !== degraded) {
    setPrevDegraded(degraded);
    if (!degraded) {
      setAttempts(0);
      setFailed(false);
    }
  }
  const latest = useRef({ router, pathname, searchString });
  useEffect(() => {
    latest.current = { router, pathname, searchString };
  });

  const fireHeal = useCallback(() => {
    const { router: r, pathname: p, searchString: s } = latest.current;
    const next = new URLSearchParams(s);
    next.set(HEAL_PARAM, String(Date.now()));
    r.replace(`${p}?${next.toString()}`, { scroll: false });
  }, []);

  // Plan de volgende poging (of de foutstaat) zolang de lijst degraded is.
  useEffect(() => {
    if (!degraded || failed) return undefined;
    let timer = null;
    let onVisible = null;
    const done = attempts >= delays.length;
    const delay = done
      ? HEAL_FINAL_SETTLE_MS
      : withJitter(delays[attempts]);

    const act = () => {
      if (done) {
        setFailed(true);
        return;
      }
      fireHeal();
      setAttempts((n) => n + 1);
    };
    const run = () => {
      if (typeof document !== "undefined" && document.hidden) {
        onVisible = () => {
          if (document.hidden) return;
          document.removeEventListener("visibilitychange", onVisible);
          onVisible = null;
          act();
        };
        document.addEventListener("visibilitychange", onVisible);
        return;
      }
      act();
    };
    timer = setTimeout(run, delay);
    return () => {
      clearTimeout(timer);
      if (onVisible) document.removeEventListener("visibilitychange", onVisible);
    };
  }, [degraded, attempts, failed, fireHeal, delays]);

  // Niet meer degraded: teller terug en _heal uit de adresbalk.
  useEffect(() => {
    if (degraded) return;
    if (typeof window === "undefined") return;
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has(HEAL_PARAM)) return;
      url.searchParams.delete(HEAL_PARAM);
      // State bewust null: Next patcht replaceState en synchroniseert dan
      // zelf de router (useSearchParams/canonicalUrl) en kopieert __NA en de
      // tree. Met de bestaande state (__NA) slaat Next die sync over en
      // houdt de router _heal vast.
      window.history.replaceState(
        null,
        "",
        `${url.pathname}${url.search}${url.hash}`,
      );
    } catch {
      // adresbalk-opschoning is cosmetisch; nooit de pagina breken.
    }
  }, [degraded, searchString]);

  // Knop "Opnieuw proberen": meteen een poging, daarna weer het schema.
  const retry = useCallback(() => {
    setFailed(false);
    fireHeal();
    setAttempts(1);
  }, [fireHeal]);

  return { failed, retry, attempts };
}
